import * as React from 'react';
import { assign, createMachine, createSchema, forwardTo } from 'xstate';
import { useMachine } from '@xstate/react';
import { diff } from 'fast-array-diff';

import { splitDateTimeString } from 'utils/bookingFlowUtils';

import {
  BookingFlowAdditionalNotes,
  BookingFlowBillingInfo,
  BookingFlowMachineEvent,
  BookingFlowUserInfo,
} from './types';
import {
  getCombinedSearchData,
  getSelectedTimeSlot,
  getShouldShowGameAreaStep,
  getBooking,
  getSelectedPackage,
  getAvailableAddOns as getAvailableAddOnsSelector,
  getSelectedAddOns,
  getSelectedReservationTags,
  getBillingInfo,
  getAdditionalNotes,
  getUserInfo,
  getPaymentData,
  getSelectedAddOnsWithGroupKeys,
  getTotalGameDuration,
} from './selectors';
import {
  fetchAvailability,
  createBooking,
  getAvailablePackages,
  updateBookingPackages,
  deleteBookingPackage,
  getAvailableAddOns,
  updateBookingAddOns,
  getAllergenReservationTags,
  getDietaryReservationTags,
  updateBookingReservationTags,
  updateBookingBilling,
  initialiseBookingPayment,
  addBookingPromoCode,
  completeBookingPaymentAsyncInit,
  completeBookingPaymentAsyncResult,
} from './apiCalls';
import { getCmsContent } from './cmsCalls';
import { useNav } from '../NavProvider';
import { useForm } from 'react-hook-form';
import {
  subscribeToFreedomPayEvents,
  triggerGetPaymentKeys,
  invokeConsumerAuthChallenge,
} from './freedomPay';
import { datalayerPush } from 'src/components/ThirdParty/GtmDatalayerPush';
import { ANALYTICS_EVENTS, useEventApi } from 'src/hooks/useEvent';

type Context = {
  events: BookingFlowMachineEvent[];
};

// eslint-disable-next-line no-console
const trackEvent = (...data: any[]) => console.log('trackEvent', ...data);

export const machine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAYgDaoDuAsgIYDGAFrmAMSo4BKcYALkaQNoAMAXUSgADqlhYOWZsJAAPRAFoALHwBMAOgCMAdgBsqvnx3KdATmN6ANCACeS5cr0aAzMrPK1fLXz1qdABx6AL7BNmiYuATE5NR0OIzMACJgAMpghGBUHACCEBAA8jj8QkggYhJSMmX2CIoArC4uGgHunt6+-kE2CghaasrNTnoufE1mWnr6oeHo2HjcsbT0GiIATmAAMqgUEEw4AJI4klgUhOkUa7RJFBwUJbIVJ9WgvYoumsbKAa18P-VeRrKGy1d71PiuNRmAL1PT1LRaMyjHQzEARebRUiUZYJVYbba7fY5ABuFCwhAoyHJkls+E4tEgDzKTyqOFkb0GzUR0L0ej4ZhMASaIMQyg0Ix0fHcFklsMmITCaLmUUW2PiYDxWx2e2YAGFUGsxGtbmA2BAsBtskzROJnmyym9EfVtLCpnD3C5eQERXUAi0XGYAYiAmNvrD6qj0SqYmqVustYTmCSyRSqYQaXSOLQAKJrNYG63lW2s9kOHQaSX6AUfUYueoeYF2JRaesaMwjFw+QJqPQBAbKSPKhYxuIrKQAW0SOAAcgBXaBgZPkynUji2Qss6T215KNQBMyuRpNAaI-Q6LQ+-rNAItoy6D77sxIweRYdY0e4idTpep1e0+k0IygiPMWW6lnUAYaN4agfN8XzmIE1hNggnjOMGQpNI49Syi+GKqh+GpfkSpLLmmGYAbm+ZrBuoEvPIzZ-C0ngWAGAbtiYLg+gMzijI47bwkYvbPoqUZvks6oaERzAACpYJOqTEBw6SZNkQGlDalRgQ6Sh6P0GiqPugSwX2WjeshpgQt8LbuCeXztrh0bvjihFyVO05gHISkcGAIg0ZpdEcjo5ZqDByJ-JYUyNrU2Hlk+Oj1MoPhQqoOhqA5YmxriVAbLcUQAEJDlA+y6jl3kFa+UCpLOVBUHAsB+Xa4ENLokL8ryfAAhYIU+gKfrwh4e6Vuo-LpZi4krNlYC5Xg5UYsVpVgLNURVTVdU8Fo6lFv5270RBzj1qYuiqB4FgIkhtS9Ro-WeAEQ1eGYo34c5GiTdNUBLXg81TWVhWUQWwHMrRO2Ovo4pjHupi3SMgxmdFQVtgKCVJR4xhpSJhVPRJUAUJOOQ5fs7meak3m+QDGmNdpEHQtoLg6J6UJ06ll5NJZjTwj8p5BI9I7PSI1AYBQMD7MpWQcAACvzgtgA1JaUw0vb6VozF-L2AL6JefLODCxhmHuHzmBG6MVZjcaS0LzCE15Pky1pO51OCaFqOGA02U752IPe5ZuAMdMJWMLYokbeE8xJs4iBAb0S1QAvmzgACq4cmlHMdpNVtWwPVZNbRTdsNJBA1-I0PaBIMXH8s6CGdnoDZygE3NOaHidSHgydS-sCcR95rcwCt6f1RtIHbU1PjOq0jhqPeAKDLyZeBhWAohbdQR9gqszGyHKxh53UTd1OHdJ2bYB-dRWebgFzYuH6-S6bCl8jF4OiXozrieJfjR9vUddB45424rsEDMFgPsMWGxiTE2tqfIGTUpjOn8MoT+-gOpCXdggCePxtB+DrJffc8JDZr2Dg3FY-9AHCwyKLPIhRiiQKHnLIubYEpO1GNXPs8UuKJXLBKVoJgLCmDpvXX+GpiE4CAckNIZDsgUKKDbc+dQDB+j8H4RK7hl7+C4gCTQMFGaSifN8Fw-DMqCPyCQi2Hkrak02mfYGShOxaCgrCRC4JEasOQv4OeiUBTxTprWaY38MoEQ0FvN6kjhHtyboufIRRYC9zWtQnOu1FBIlcKZaedMoQhjUD6Xiti6xK0sLwyYX98E-wMQEpuURgkiPjmEipx9pFWLqD2A8dMJjX3qPWXwt0uJwj9O4cEkwOowT7Po-xs5YBgDWEcAAZqgYBoDwHmMHnE3oUJmh1j+FCKUgZ4TONqE7dB8CfBGCduoNpwznqjPGVMmZzAQFgDASTdaFioGUycM6KUPZbrqE9AYFBE9Upti8FoT0R59BTDOaHMZEycDTIJqY+ZdTwKIjFBMbZuhP5ApgZknszgDCGHhAGRogcil+OerAWc45xyXFsLMu58LYmyztoYDQXgIbmADrpR+yFOwAiuhheKAY4QIiJUqdehDcRkopVS-YeV+bSVQLvOlTyaF20lGKdhnpTIwgNvULFIZtBeCmH2P55hV4ioIQIjQErKVrGpcwGV0c5Vx0hVcxViyGW7SslBeCSJWjosaLqiE-QOoWF8KoQYA5fFjRKVaqVzARbZDYJC0krJpKCwRXLBEV1vjVyRCMRxK9MnTwrD8Wmxl4S8nBSsGNNr9gpHjRwRN4zk1blTVAdNucJ76X8DfUYIYvgfEyQYA8oasKOA6RG4lUb-HVttTgHIIgRCEFsCA1A45UD6ggNLelttdowi5N4fc-IkSdkSpkgV+k-B-PBACS+lbxXkutbO+di7l35jXRu1Oq0M7tt3U0K6ExawBkmPoC8XLdKaBvKlAEbTdIGDwWa4p06H2xrnQupdK732oE3bU7dMiDAQlusMVxmqhSZPPBBkKTgYQ-H5HuO9GoZ2wqJg83D9SJ77Xiq0GxUwBQoNpvCcUfJ+TmF6lx+jGg0zpjwFcml9yIFKqWaKVsfJzyOCCnI7wmSYSaCnuk2EPsOrick1EGTJjmPybdTu3oLYeI61uuhFsMEfSJT0oKeKx6nB0cjSbXEuAThnCwBIFuFBbCThwBwYBIWwscCOP59MYyIA-uWSFcUdMTlNBbEKEwzmCVXT+EvVKqM9HeY3r544UgAtBagBLULYBwuRdq+F2LFX4u5WYDhhT7reh1ivishE4xP71mc94Jp3x4F1kSj2T04m+aNYi8wfAGxICrpq2wXY65WPQLy6lCejhTKIhGLDJT6gMHUa6p4Hxk6fMalm9FpjZiktKHLB1ETTtTC60cSgpw8UabmAnp-XiE6EMkokrdur82cCLbAMt8cNWavRYANJgFsJnTrVnECAiuphPk3JUoCmcyYWxxgl6mTGDxmbUXwf7ChzDmrHXLN4YVu4fQl9F5NG6OZXs8ilZQkwYGXQ4mqCrsXZwHelP6s3PFxwfU44RfeUS5tymOT6H8jcKzwI3hOLIXzdoII7YeceIeiVsVGohey8yM3arUuGvRfp4DZVHqFajHrFsp2TtzwE8SlBQIgRSeenMKERUOAsNwFkKJKdzkGf1MUKlZocCEGymQT6PO142i3g8JfTwM38Taij01Xpp3eT3XbB85PoVvef0wQibCAPxNfjz3LAvBShNtI9ACQtB5oaSilLpRwPxTXh+uy9Uq+VCoN9zh6Zlhh2yGDhEFH0LC8uKJ4yGKu4nsa4xyuP+JAYIT8fdICzoHVLyAv0jw3QzTfjCsH6Vm7h9t9vEz8yh2Q62nqHPCg9PF7gO94SvZY3FqgSluu8D+DgHgisfwpk1cqgSsrQl4us2Shy54QKCIXC4mQi8A9uime0YokGfIAIXwMGXEHwB4QqIwpkT4-Qpg4mQB5SESwioBdQPOFYmiPYiIdYjMWuuy3YV02EbEyiQU-QNBzq0KqAjBiCgm18N4uC+4UUiATCYo+snyFBgwLY4mM64hIwLQ2EKB2C6SM8XK2y+kowKsast41+GMt+Em5IUmUAVymhyK4IiURygKTMXKM+V0qmY2E8QKfw4mfmLWgWYuc2jBF+bY8UKmQkvO8CzmAwfobuaSQofyFOIRWBXWGOWhWquhPw+hzmNeLQPGgyBgMEaMV2VhZucuwR0W4hgQqWJgMGe4kwqi5kEwe+IU+m-QJgl2wOEeEkQu0KFolK7qli4E2C4ofsgK+4wKXBiAAIfIzKQUSIExRkgewQQAA */
    id: 'bookingFlowMachine',
    predictableActionArguments: true,
    preserveActionOrder: true,
    tsTypes: {} as import('./machine.typegen').Typegen0,
    initial: 'preLoad',
    schema: {
      events: createSchema<BookingFlowMachineEvent>(),
      context: createSchema<Context>(),
    },
    on: {
      onResetFlow: {
        actions: ['resetEvents', 'resetForms'],
        target: 'preLoad',
      },
      onDeSelectAddOn: {
        actions: ['storeEvent'],
      },
    },
    states: {
      preLoad: {
        invoke: {
          src: 'fetchAvailability',
          id: 'fetchAvailability',
        },
        on: {
          onInitialSearchData: {
            actions: ['storeEvent', forwardTo('fetchAvailability')],
          },
          onAvailabilityFetched: {
            target: 'time',
            actions: ['storeEvent'],
          },
          onCorporateRedirect: {
            actions: ['storeEvent', 'redirectToGroupBookings'],
          },
          onAvailabilityFetchError: {
            actions: ['storeEvent'],
          },
        },
      },
      time: {
        invoke: {
          src: 'fetchAvailability',
          id: 'fetchAvailability',
        },
        on: {
          onNudgeAvailability: {
            actions: ['storeEvent', forwardTo('fetchAvailability')],
          },
          onAvailabilityFetched: {
            target: 'time',
            actions: ['storeEvent'],
          },
          onAvailabilityFetchError: {
            actions: ['storeEvent'],
          },
          onTimeSlotSelected: {
            actions: ['storeEvent'],
          },
          onNextStep: {
            actions: ['storeEvent'],
            target: 'creatingBooking',
            cond: 'hasSelectedTimeSlot',
          },
        },
      },
      creatingBooking: {
        invoke: {
          src: 'createBooking',
          id: 'createBooking',
        },
        on: {
          onCreateBookingSuccess: [
            {
              actions: ['storeEvent'],
              target: 'gameArea',
              cond: 'shouldShowGameAreaStep',
            },
            {
              actions: ['storeEvent'],
              target: 'package',
            },
          ],
          onCreateBookingError: {
            actions: ['storeEvent'],
            target: 'time',
          },
        },
      },
      gameArea: {
        on: {
          onNextStep: {
            actions: ['storeEvent'],
            target: 'package',
          },
        },
      },
      package: {
        on: {
          onSelectPackage: {
            actions: ['storeEvent'],
          },
          onNextStep: {
            cond: 'hasSelectedPackageIfRequired',
            actions: ['storeEvent'],
            target: 'updatingPackage',
          },
        },
      },
      updatingPackage: {
        invoke: {
          src: 'updatePackage',
          id: 'updatePackage',
        },
        on: {
          onUpdatePackageSuccess: [
            {
              cond: 'hasAvailableAddOns',
              actions: ['storeEvent'],
              target: 'addons',
            },
            {
              actions: ['storeEvent'],
              target: 'userInfo',
            },
          ],
          onUpdatePackageError: {
            actions: ['storeEvent'],
            target: 'package',
          },
        },
      },
      addons: {
        on: {
          onPrevStep: {
            actions: ['storeEvent'],
            target: 'package',
          },
          onSelectAddOn: {
            actions: ['storeEvent'],
          },
          onDeSelectAddOn: {
            actions: ['storeEvent'],
          },
          onNextStep: {
            actions: ['storeEvent'],
            target: 'updatingAddOns',
          },
        },
      },
      updatingAddOns: {
        invoke: {
          src: 'updateAddOns',
          id: 'updateAddOns',
        },
        on: {
          onUpdateAddOnsSuccess: {
            actions: ['storeEvent'],
            target: 'userInfo',
          },
          onUpdateAddOnsError: {
            actions: ['storeEvent'],
            target: 'addons',
          },
        },
      },
      userInfo: {
        on: {
          onPrevStep: [
            {
              cond: 'hasAvailableAddOns',
              actions: ['storeEvent'],
              target: 'addons',
            },
            {
              actions: ['storeEvent'],
              target: 'package',
            },
          ],
          onNextStep: {
            cond: 'isUserInfoValid',
            actions: [
              'storeEvent',
              'captureUserInfo',
              'recordPersonalInfoCompleted',
            ],
            target: 'summary',
          },
        },
      },
      summary: {
        invoke: {
          src: 'addPromoCodeOnChange',
          id: 'addPromoCodeOnChange',
        },
        on: {
          onPrevStep: {
            actions: ['storeEvent'],
            target: 'userInfo',
          },
          onBackToPackageStep: {
            actions: ['storeEvent'],
            target: 'package',
          },
          onBackToUserInfoStep: {
            actions: ['storeEvent'],
            target: 'userInfo',
          },
          onSelectReservationTag: {
            actions: ['storeEvent'],
          },
          onDeSelectReservationTag: {
            actions: ['storeEvent'],
          },
          onApplyPromoCode: {
            actions: ['storeEvent', forwardTo('addPromoCodeOnChange')],
          },
          onApplyPromoCodeSuccess: {
            actions: ['storeEvent'],
          },
          onApplyPromoCodeError: {
            actions: ['storeEvent'],
          },
          onNextStep: {
            actions: ['storeEvent', 'captureAdditionalNotes'],
            target: 'billingInfo',
          },
        },
      },
      billingInfo: {
        on: {
          onPrevStep: {
            actions: ['storeEvent'],
            target: 'summary',
          },
          onNextStep: {
            cond: 'isBillingInfoValid',
            actions: ['storeEvent', 'captureBillingInfo'],
            target: 'initialisingPayment',
          },
        },
      },
      initialisingPayment: {
        invoke: {
          src: 'initialisePayment',
          id: 'initialisePayment',
        },
        on: {
          onPaymentInitialised: {
            actions: ['storeEvent'],
            target: 'payment',
          },
          onPaymentInitialisationError: {
            actions: ['storeEvent'],
            target: 'billingInfo',
          },
        },
      },
      payment: {
        invoke: {
          src: 'freedomPayIntegration',
          id: 'freedomPayIntegration',
        },
        on: {
          onFreedomPayReady: {
            actions: ['storeEvent'],
          },
          onNextStep: {
            actions: ['storeEvent', 'triggerGetPaymentKeys'],
          },
          onFreedomPayPaymentKeys: {
            cond: 'noPaymentError',
            actions: ['storeEvent'],
            target: 'completingPayment',
          },
          onFreedomPayError: {
            actions: ['storeEvent'],
          },
        },
      },
      completingPayment: {
        invoke: {
          src: 'completePayment',
          id: 'completePayment',
        },
        on: {
          onPaymentCompleted: {
            actions: ['storeEvent', 'handleConfirmation'],
            target: 'confirmation',
          },
          onPaymentError: {
            actions: ['storeEvent'],
            target: 'payment',
          },
        },
      },
      confirmation: {},
    },
  },
  {
    actions: {
      storeEvent: assign({
        events: (context, event) => [...context.events, event],
      }),
      resetEvents: assign({
        events: () => [],
      }),
    },
    services: {},
    guards: {
      hasSelectedPackageIfRequired: (context, event) => {
        const selectedPackage = getSelectedPackage([...context.events, event]);
        const booking = getBooking([...context.events, event]);
        return Boolean(selectedPackage || !booking?.isPackageRequired);
      },
      hasSelectedTimeSlot: (context, event) => {
        const selectedTimeSlot = getSelectedTimeSlot([
          ...context.events,
          event,
        ]);
        return !!selectedTimeSlot;
      },
      shouldShowGameAreaStep: (context, event) => {
        const shouldShowGameAreaStep = getShouldShowGameAreaStep([
          ...context.events,
          event,
        ]);

        return shouldShowGameAreaStep;
      },
      hasAvailableAddOns: (context, event) => {
        const addOns = getAvailableAddOnsSelector([...context.events, event]);
        return Boolean(addOns?.length);
      },
      noPaymentError: (context, event) => {
        return !event.paymentAttributes?.authenticateResponse?.errorDesc;
      },
    },
  }
);

export const steps = Object.values(machine.states)
  .map((s) => s.key)
  .filter(
    (s) =>
      s !== 'creatingBooking' &&
      s !== 'updatingPackage' &&
      s !== 'updatingAddOns' &&
      s !== 'initialisingPayment'
  );

const userDetailsDefaultValues: BookingFlowUserInfo = {
  firstName: '',
  lastName: '',
  email: '',
  dob: '',
  mobileNumber: {
    code: '+44',
    number: '',
  },
  newsletter: false,
};

export const useBookingFlowMachineInstance = () => {
  const { setIsBookingModalOpen } = useNav();
  const { createEvent, error } = useEventApi();

  const userInfoForm = useForm<BookingFlowUserInfo>({
    defaultValues: userDetailsDefaultValues,
    mode: 'onBlur',
  });

  const additionalNotesForm = useForm<BookingFlowAdditionalNotes>({
    defaultValues: { notes: '' },
    mode: 'onBlur',
  });

  const billingInfoForm = useForm<BookingFlowBillingInfo>({
    defaultValues: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: '',
      name: '',
      postcode: '',
      termsAndConditions: false,
      cancellationPolicy: false,
      privacyPolicy: false,
      ageConfirmation: false,
    },
    mode: 'onBlur',
  });

  const freedomPayIframeId = React.useRef<string | null>(null);

  const [state, send, interpreter] = useMachine(machine, {
    actions: {
      recordPersonalInfoCompleted: () => {
        createEvent(ANALYTICS_EVENTS.PERSONAL_INFO_COMPLETED);
      },
      redirectToGroupBookings: () => {
        window.location.replace(
          process.env.NEXT_PUBLIC_GROUP_BOOKINGS_REDIRECT_URL ||
            'https://clays.bar/group-bookings'
        );
        setIsBookingModalOpen(false);
      },
      captureUserInfo: assign({
        events: (context) => [
          ...context.events,
          {
            type: 'onUserInfoUpdated' as const,
            userInfo: userInfoForm.getValues(),
          },
        ],
      }),
      captureAdditionalNotes: assign({
        events: (context) => [
          ...context.events,
          {
            type: 'onAdditionalNotesUpdated' as const,
            additionalNotes: additionalNotesForm.getValues(),
          },
        ],
      }),
      captureBillingInfo: assign({
        events: (context) => [
          ...context.events,
          {
            type: 'onBillingInfoUpdated' as const,
            billingInfo: billingInfoForm.getValues(),
          },
        ],
      }),
      triggerGetPaymentKeys: () => {
        if (freedomPayIframeId.current) {
          triggerGetPaymentKeys(freedomPayIframeId.current);
        } else {
          console.error('No freedom pay iframe found');
        }
      },
      handleConfirmation: (context) => {
        if (typeof window !== 'undefined') {
          const booking = getBooking(context.events);
          const bookingDetails = {
            booking: booking,
            totalGameDuration: getTotalGameDuration(context.events),
            selectedPackage: getSelectedPackage(context.events),
          };
          // For now we send this event here as well
          // Once we move everything to sGTM we can remove this
          datalayerPush({
            event: 'paymentCompleted',
            bookingId: booking?.bookingId,
            venueName: booking?.venueName,
            venueId: booking?.venueId,
            email: booking?.billing?.email,
            firstName: booking?.billing?.firstName,
            lastName: booking?.billing?.lastName,
            fullName: `${booking?.billing?.firstName} ${booking?.billing?.lastName}`,
            partySize: booking?.partySize,
            charge: booking?.charge,
            totalCost: booking?.totalCost,
          });
          localStorage.setItem(
            'bookingDetails',
            JSON.stringify(bookingDetails)
          );
          window.location.replace(
            process.env.URL_SUCCESSFUL_BOOKING || '/successful-booking'
          );
        }
      },
      resetForms: () => {
        userInfoForm.reset();
        additionalNotesForm.reset();
        billingInfoForm.reset();
      },
    },
    guards: {
      isUserInfoValid: () => {
        return userInfoForm.formState.isValid;
      },
      isBillingInfoValid: () => {
        return billingInfoForm.formState.isValid;
      },
    },
    services: {
      fetchAvailability: () => {
        return (send, receive) => {
          receive(async (event: BookingFlowMachineEvent) => {
            if (event.type === 'onInitialSearchData') {
              const res = await fetchAvailability(event.searchData);

              if (res.type === 'ok') {
                send({
                  type: 'onAvailabilityFetched',
                  threeDayAvailability: res.payload,
                });
              } else if (res.type === 'corporateRedirect') {
                send({ type: 'onCorporateRedirect' });
              } else {
                send({
                  type: 'onAvailabilityFetchError',
                });
              }
            }

            if (event.type === 'onNudgeAvailability') {
              const { events } = interpreter.getSnapshot().context;

              const searchData = getCombinedSearchData(events);

              if (!searchData) {
                send({
                  type: 'onAvailabilityFetchError',
                });
                return;
              }

              const res = await fetchAvailability(searchData);

              if (res.type === 'ok') {
                send({
                  type: 'onAvailabilityFetched',
                  threeDayAvailability: res.payload,
                });
              } else {
                send({
                  type: 'onAvailabilityFetchError',
                });
              }
            }
          });
        };
      },
      createBooking: (context) => {
        return async (send) => {
          const selectedTimeSlot = getSelectedTimeSlot(context.events);
          const combinedSearchData = getCombinedSearchData(context.events);

          if (
            !selectedTimeSlot?.accessRule?.venueId ||
            !selectedTimeSlot.accessPersistentId ||
            !selectedTimeSlot.shiftPersistentId ||
            !combinedSearchData
          ) {
            return;
          }

          const dateTimeString = selectedTimeSlot.timeIso;
          const { date, time } = splitDateTimeString(dateTimeString);

          const [
            createBookingRes,
            cmsContentRes,
            allergenReservationTagsRes,
            dietryReservationTagsRes,
          ] = await Promise.all([
            createBooking({
              venueId: selectedTimeSlot.accessRule.venueId,
              accessPersistentId: selectedTimeSlot.accessPersistentId,
              occasionId: combinedSearchData.Occasion.id,
              partySize: combinedSearchData.Who,
              reservationDate: date,
              reservationTime: time,
              shiftPersistentId: selectedTimeSlot.shiftPersistentId,
            }),
            getCmsContent(),
            getAllergenReservationTags(),
            getDietaryReservationTags(),
          ]);

          if (
            createBookingRes.type === 'ok' &&
            cmsContentRes.type === 'ok' &&
            allergenReservationTagsRes.type === 'ok' &&
            dietryReservationTagsRes.type === 'ok'
          ) {
            const availablePackagesRes = await getAvailablePackages({
              bookingId: createBookingRes.payload.bookingId,
            });

            if (availablePackagesRes.type === 'ok') {
              send({
                type: 'onCreateBookingSuccess',
                booking: createBookingRes.payload,
                cmsContent: cmsContentRes.payload,
                availablePackages: availablePackagesRes.payload,
                allergenReservationTags: allergenReservationTagsRes.payload,
                dietaryReservationTags: dietryReservationTagsRes.payload,
              });
              return;
            }
          }

          send({ type: 'onCreateBookingError' });
        };
      },
      updatePackage: (context) => {
        return async (send) => {
          let booking = getBooking(context.events);
          const selectedPackage = getSelectedPackage(context.events);

          if (!booking) {
            send({
              type: 'onUpdatePackageError',
            });
            return;
          }

          const { added, removed } = diff(
            booking.packages,
            selectedPackage ? [selectedPackage] : [],
            (a, b) => a.packageId === b.packageId
          );

          if (removed.length) {
            for (const p of removed) {
              const updatePackagesRes = await deleteBookingPackage({
                bookingId: booking.bookingId,
                packageId: p.packageId,
              });

              if (updatePackagesRes.type === 'ok') {
                booking = updatePackagesRes.payload;
              } else {
                send({ type: 'onUpdatePackageError' });
                return;
              }
            }
          }
          if (added.length) {
            const updatePackagesRes = await updateBookingPackages({
              bookingId: booking.bookingId,
              packageIds: added.map((p) => p.packageId),
            });

            if (updatePackagesRes.type === 'ok') {
              booking = updatePackagesRes.payload;
            } else {
              send({ type: 'onUpdatePackageError' });
              return;
            }
          }

          const availableAddOnsRes = await getAvailableAddOns({
            bookingId: booking.bookingId,
          });

          if (availableAddOnsRes.type !== 'ok') {
            send({
              type: 'onUpdatePackageError',
            });
            return;
          }

          if (added.length || removed.length) {
            // Clear any add-ons that are selected from our local state - the
            // server will clear add-ons from the booking whenever a package
            // changes so we don't need to make an explicit call to do this.
            const selectedAddOns = getSelectedAddOnsWithGroupKeys(
              context.events
            );
            selectedAddOns.forEach(({ addOn, addOnGroupKey }) => {
              send({
                type: 'onDeSelectAddOn',
                addOn,
                addOnGroupKey,
              });
            });
          }

          send({
            type: 'onUpdatePackageSuccess',
            booking,
            availableAddOns: availableAddOnsRes.payload,
          });
        };
      },
      updateAddOns: (context) => {
        return async (send) => {
          const booking = getBooking(context.events);
          if (!booking) {
            send({
              type: 'onUpdateAddOnsError',
            });
            return;
          }
          const selectedAddOns = getSelectedAddOns(context.events);

          const { added, removed } = diff(
            booking.addOns,
            selectedAddOns,
            (a, b) => a.addOnType === b.addOnType
          );

          // Skip if no changes
          if (!added.length && !removed.length) {
            send({
              type: 'onUpdateAddOnsSuccess',
              booking,
            });
            return;
          }

          const res = await updateBookingAddOns({
            bookingId: booking.bookingId,
            addOnTypes: selectedAddOns.map((a) => a.addOnType),
          });

          if (res.type === 'error') {
            send({
              type: 'onUpdateAddOnsError',
            });
            return;
          } else {
            send({
              type: 'onUpdateAddOnsSuccess',
              booking: res.payload,
            });
            return;
          }
        };
      },
      initialisePayment: (context) => {
        return async (send) => {
          let booking = getBooking(context.events);
          const billingInfo = getBillingInfo(context.events);
          const additionalNotes = getAdditionalNotes(context.events);
          const userInfo = getUserInfo(context.events);
          if (!booking || !billingInfo || !additionalNotes || !userInfo) {
            send({
              type: 'onPaymentInitialisationError',
            });
            return;
          }

          // Update the reservation tags.
          const selectedReservationTags = getSelectedReservationTags(
            context.events
          );
          const { added, removed } = diff(
            booking.reservationTags,
            selectedReservationTags,
            (a, b) => a.id === b.id
          );
          if (added.length || removed.length) {
            const updateBookingAddOnsRes = await updateBookingReservationTags({
              bookingId: booking.bookingId,
              reservationTagIds: selectedReservationTags.map((t) => t.id),
            });

            if (updateBookingAddOnsRes.type === 'ok') {
              booking = updateBookingAddOnsRes.payload;
            } else {
              send({ type: 'onPaymentInitialisationError' });
              return;
            }
          }

          // Update the billing info.
          const updateBillingInfoRes = await updateBookingBilling({
            bookingId: booking.bookingId,
            additionalNotes,
            billingInfo,
            userInfo,
          });

          if (updateBillingInfoRes.type === 'error') {
            send({ type: 'onPaymentInitialisationError' });
            return;
          }

          if (
            updateBillingInfoRes.payload.billing?.status !==
            'payment_to_be_initialised'
          ) {
            console.warn('Billing status is not payment_to_be_initialised');
          }

          booking = updateBillingInfoRes.payload;

          // Now initialise the payment.
          const initialisePaymentRes = await initialiseBookingPayment({
            bookingId: booking.bookingId,
          });

          if (initialisePaymentRes.type === 'error') {
            send({ type: 'onPaymentInitialisationError' });
            return;
          }

          send({
            type: 'onPaymentInitialised',
            booking,
            bookingPayment: initialisePaymentRes.payload,
          });
        };
      },
      freedomPayIntegration: () => {
        let ready = false;
        let rejectPayment = false;

        return (send) => {
          const sub = subscribeToFreedomPayEvents((event) => {
            switch (event.type) {
              case 'onSetIframeHeight': {
                if (!ready) {
                  send({ type: 'onFreedomPayReady' });
                  ready = true;
                }
                break;
              }

              case 'onPaymentKeys':
                if (rejectPayment) {
                  send({
                    type: 'onFreedomPayError',
                    errors: 'rejected',
                  });
                } else {
                  send({
                    type: 'onFreedomPayPaymentKeys',
                    paymentAttributes: event.attributes,
                    paymentKeys: event.paymentKeys,
                  });
                }
                break;

              case 'onError':
                send({
                  type: 'onFreedomPayError',
                  errors: event.errors,
                });
                break;

              case '3dsResponse':
                const isCase1 = event.authenticationStatus === 'Y';
                const isCase2 = event.authenticationStatus === 'A';
                const isCase3 = event.authenticationStatus === 'N';
                const isCase4 = event.authenticationStatus === 'U';
                const isCase5 =
                  !event.authenticationStatus &&
                  event.enrolled === 'U' &&
                  (event.eciFlag === '07' || event.eciFlag === '00');
                const isCase6 = event.authenticationStatus === 'R';

                // Manage all cases explicitly and fallback to an error if we
                // don't recognise the case.
                if (isCase3 || isCase4 || isCase6) {
                  rejectPayment = true;
                } else if (isCase1 || isCase2 || isCase5) {
                  rejectPayment = false;
                } else {
                  rejectPayment = true;
                }
                break;

              case '3dsResponseParsingError':
                rejectPayment = true;
                break;

              default:
                break;
            }
          });

          return () => {
            sub.unsubscribe();
          };
        };
      },
      completePayment: (context) => {
        return async (send) => {
          const booking = getBooking(context.events);
          const { paymentAttributes, paymentKeys } = getPaymentData(
            context.events
          );
          if (!booking || !paymentAttributes || !paymentKeys) {
            send({ type: 'onPaymentError', softDecline: false });
            return;
          }

          const initRes = await completeBookingPaymentAsyncInit({
            bookingId: booking.bookingId,
            attributes: paymentAttributes,
            paymentKeys: paymentKeys,
          });

          if (initRes.type === 'error') {
            send({ type: 'onPaymentError', softDecline: false });
          }

          // Start polling for payment completion.
          let resultRes = await completeBookingPaymentAsyncResult({
            bookingId: booking.bookingId,
          });

          let timeout = 1000;

          while (resultRes.type === 'incomplete') {
            await new Promise((resolve) => setTimeout(resolve, timeout));
            resultRes = await completeBookingPaymentAsyncResult({
              bookingId: booking.bookingId,
            });
            if (timeout < 7000) {
              timeout *= 1.3;
            }
          }

          if (resultRes.type === 'ok') {
            send({
              type: 'onPaymentCompleted',
              booking: resultRes.payload,
            });
          } else {
            if (resultRes.errorClass === 'softDecline') {
              invokeConsumerAuthChallenge();
            }
            send({
              type: 'onPaymentError',
              softDecline: resultRes.errorClass === 'softDecline',
            });
          }
        };
      },
      addPromoCodeOnChange: (context) => {
        return (send, receive) => {
          receive(async (event: BookingFlowMachineEvent) => {
            if (event.type === 'onApplyPromoCode') {
              const booking = getBooking(context.events);
              if (!booking) {
                send({ type: 'onApplyPromoCodeError' });
                return;
              }

              const res = await addBookingPromoCode({
                bookingId: booking.bookingId,
                promoCode: event.promoCode,
              });

              if (res.type === 'error') {
                send({ type: 'onApplyPromoCodeError' });
                return;
              } else {
                send({
                  type: 'onApplyPromoCodeSuccess',
                  booking: res.payload,
                });
                return;
              }
            }
          });
        };
      },
    },
    context: {
      events: [],
    },
  });

  React.useEffect(() => {
    const subscription = interpreter.subscribe((state) => {
      if (state.event.type === 'onNextStep') {
        datalayerPush({
          event: 'nextStep',
          step: state.value,
        });
      }

      if (state.event.type === 'onPrevStep') {
        datalayerPush({
          event: 'prevStep',
          step: state.value,
        });
      }

      if (state.event.type === 'onTimeSlotSelected') {
        datalayerPush({
          event: 'timeSlotSelected',
          slotTme: state.event.timeSlot.timeIso,
          slotCharge: state.event.timeSlot.charge,
          bookingFlowType: state.event.bookingFlowType,
        });
      }

      if (state.event.type === 'onSelectPackage') {
        const selectedPackage = getSelectedPackage(state.context.events);
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'packageSelected',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
          packageId: selectedPackage?.packageId,
          packageName: selectedPackage?.name,
          packageCharge: selectedPackage?.charge,
        });
      }

      if (state.event.type === 'onSelectAddOn') {
        const selectedAddOns = getSelectedAddOns(state.context.events);
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'addOnSelected',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
          addOns: selectedAddOns.map((a) => ({
            addOnId: a.addOnType,
            addOnName: a.name,
            addOnCharge: a.charge,
          })),
        });
      }

      if (state.event.type === 'onDeSelectAddOn') {
        const selectedAddOns = getSelectedAddOns(state.context.events);
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'addOnDeselected',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
          addOns: selectedAddOns.map((a) => ({
            addOnId: a.addOnType,
            addOnName: a.name,
            addOnCharge: a.charge,
          })),
        });
      }

      if (state.event.type === 'onUserInfoUpdated') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'userInfoUpdated',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onBillingInfoUpdated') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'billingInfoUpdated',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onAdditionalNotesUpdated') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'additionalNotesUpdated',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onApplyPromoCode') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'promoCodeApplied',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
          promoCode: state.event.promoCode,
        });
      }

      if (state.event.type === 'onApplyPromoCodeSuccess') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'promoCodeAppliedSuccess',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onApplyPromoCodeError') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'promoCodeAppliedError',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onPaymentInitialised') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'paymentInitialised',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onPaymentError') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'paymentError',
          bookingId: booking?.bookingId,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onCreateBookingSuccess') {
        const booking = getBooking(state.context.events);
        datalayerPush({
          event: 'bookingCreated',
          bookingId: booking?.bookingId,
          venueName: booking?.venueName,
          venueId: booking?.venueId,
        });
      }

      if (state.event.type === 'onCreateBookingError') {
        datalayerPush({
          event: 'bookingCreatedError',
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [interpreter, createEvent]);

  return {
    state,
    send,
    userInfoForm,
    additionalNotesForm,
    billingInfoForm,
    freedomPayIframeId,
  };
};
